import { useState } from 'react'
import { Box, TextField, Button, Typography, Chip } from '@mui/material'

import { ModalWrapper } from 'src/components/modal-wrapper'
import { useStyles } from './../styles'
import { useTranslation } from 'react-i18next'
import { ISettingsDescriptionEntityPayload } from 'src/types'
import { capitalizeFirstLetter } from 'src/common/utils'

interface IProps {
  descriptions: ISettingsDescriptionEntityPayload | undefined
  handleUpdate: (desc: Partial<ISettingsDescriptionEntityPayload>) => void
  isLoading?: boolean
}

export const DescriptionEntityPrompts = ({ descriptions, handleUpdate, isLoading }: IProps) => {
  const [field, setField] = useState<keyof ISettingsDescriptionEntityPayload | null>(null)
  const [value, setValue] = useState<string | null>(null)

  const [t] = useTranslation()

  const classes = useStyles()

  const handleSave = () => {
    if (field) {
      handleUpdate({ [field]: value })
    }

    setField(null)
    setValue(null)
  }

  const handleOpen = (field: keyof ISettingsDescriptionEntityPayload) => {
    if (descriptions) {
      setField(field)
      setValue(descriptions[field])
    }
  }

  const handleClose = () => {
    setField(null)
    setValue(null)
  }

  if (!descriptions) {
    return
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          width: '100%',
          justifyContent: 'start',
          alignItems: 'center',
        }}
      >
        {Object.keys(descriptions).map((item) => (
          <Box
            onClick={() => {
              handleOpen(item as keyof ISettingsDescriptionEntityPayload)
            }}
            sx={{ cursor: 'pointer' }}
          >
            <Chip key={item} label={item} sx={{ fontWeight: 500 }} />
          </Box>
        ))}
      </Box>
      <ModalWrapper className={classes.modalWrapper} open={!!field} onClose={handleClose}>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            gap: '20px',
            width: '100%',
            flexDirection: 'column',
            justifyContent: 'space-between',
            pt: '30px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <Typography
              fontWeight={600}
              fontSize={'14px'}
              sx={{ width: '100%', textAlign: 'center' }}
            >{`${capitalizeFirstLetter(field || '')} Description Prompt`}</Typography>
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={isLoading}
              multiline
              maxRows={12}
              minRows={6}
              InputProps={{
                sx: {
                  fontSize: '14px',
                },
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '25px',
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button variant="outlined" size="small" onClick={handleClose} disabled={isLoading}>
              {t('cancel-button-text.v')}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={isLoading || value === descriptions[field!]}
            >
              {t('save-button-text.v')}
            </Button>
          </Box>
        </Box>
      </ModalWrapper>
    </>
  )
}
